import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";

export interface Category {
  id: number;
  name: string;
  color: string;
  sort_order: number;
  active: boolean;
  created_at: string;
}

// Fallback colors predefinidos para categorías nuevas
const COLOR_POOL = [
  "bg-blue-600",
  "bg-slate-700",
  "bg-emerald-600",
  "bg-orange-500",
  "bg-rose-600",
  "bg-amber-600",
  "bg-red-500",
  "bg-blue-700",
  "bg-teal-600",
  "bg-indigo-600",
  "bg-cyan-600",
  "bg-lime-600",
  "bg-pink-600",
  "bg-violet-600",
  "bg-fuchsia-600",
];

const DEFAULT_CATEGORIES: Category[] = [
  { id: 1, name: "Aerolíneas", color: "bg-blue-600", sort_order: 1, active: true, created_at: "" },
  { id: 2, name: "Aeropuertos", color: "bg-slate-700", sort_order: 2, active: true, created_at: "" },
  { id: 3, name: "Sostenibilidad", color: "bg-emerald-600", sort_order: 3, active: true, created_at: "" },
  { id: 4, name: "Turismo", color: "bg-orange-500", sort_order: 4, active: true, created_at: "" },
  { id: 5, name: "Entrevistas", color: "bg-rose-600", sort_order: 5, active: true, created_at: "" },
  { id: 6, name: "Tendencias", color: "bg-amber-600", sort_order: 6, active: true, created_at: "" },
  { id: 7, name: "Carga Aérea y Logística", color: "bg-red-500", sort_order: 7, active: true, created_at: "" },
  { id: 8, name: "Estadísticas", color: "bg-blue-700", sort_order: 8, active: true, created_at: "" },
  { id: 9, name: "Educación", color: "bg-teal-600", sort_order: 9, active: true, created_at: "" },
  { id: 10, name: "Nacional", color: "bg-orange-600", sort_order: 10, active: true, created_at: "" },
  { id: 11, name: "Internacional", color: "bg-yellow-600", sort_order: 11, active: true, created_at: "" },
];

export function useCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: supaError } = await supabase
        .from("categories")
        .select("*")
        .eq("active", true)
        .order("sort_order", { ascending: true });

      if (supaError) throw supaError;

      if (data && data.length > 0) {
        setCategories(data);
      } else {
        // Fallback a defaults si la tabla está vacía
        setCategories(DEFAULT_CATEGORIES);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar categorías");
      setCategories(DEFAULT_CATEGORIES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const createCategory = async (name: string, color?: string) => {
    const existingColors = categories.map((c) => c.color);
    const newColor =
      color ||
      COLOR_POOL.find((c) => !existingColors.includes(c)) ||
      "bg-slate-600";
    const maxOrder = categories.length > 0
      ? Math.max(...categories.map((c) => c.sort_order))
      : 0;

    const { data, error: supaError } = await supabase
      .from("categories")
      .insert([{ name, color: newColor, sort_order: maxOrder + 1, active: true }])
      .select()
      .single();

    if (supaError) throw supaError;
    await fetchCategories();
    return data;
  };

  const updateCategory = async (id: number, updates: Partial<Category>) => {
    const { error: supaError } = await supabase
      .from("categories")
      .update(updates)
      .eq("id", id);

    if (supaError) throw supaError;
    await fetchCategories();
  };

  const deleteCategory = async (id: number) => {
    const { error: supaError } = await supabase
      .from("categories")
      .delete()
      .eq("id", id);

    if (supaError) throw supaError;
    await fetchCategories();
  };

  const reorderCategories = async (orderedIds: number[]) => {
    const updates = orderedIds.map((id, idx) =>
      supabase.from("categories").update({ sort_order: idx + 1 }).eq("id", id)
    );
    await Promise.all(updates);
    await fetchCategories();
  };

  // Map name -> color para compatibilidad con código existente
  const categoryColors: Record<string, string> = {};
  categories.forEach((c) => {
    categoryColors[c.name] = c.color;
  });

  const standardCategories = categories.map((c) => c.name);

  return {
    categories,
    loading,
    error,
    categoryColors,
    standardCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    refetch: fetchCategories,
  };
}